/**
 * Three-way merge of text by paragraph (RFC 0313 3k).
 *
 * Automerge merges two rewrites of one paragraph without a conflict, by keeping
 * both sets of characters interleaved. Nobody notices that until they read it.
 * So before a branch merges, each side's changes since the base are lined up by
 * paragraph: a paragraph changed on one side takes that side, and one changed on
 * both is an OVERLAP that someone has to pick. PURE.
 */

export const splitParagraphs = (text: string): string[] => text.split(/\n{2,}/)
export const joinParagraphs = (ps: string[]): string => ps.join('\n\n')

/** Longest common subsequence alignment: pairs of [baseIndex, otherIndex]. */
function lcs(a: string[], b: string[]): [number, number][] {
  const n = a.length
  const m = b.length
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] =
        a[i] === b[j]
          ? (dp[i + 1]![j + 1] ?? 0) + 1
          : Math.max(dp[i + 1]![j] ?? 0, dp[i]![j + 1] ?? 0)
    }
  }
  const out: [number, number][] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push([i, j])
      i++
      j++
    } else if ((dp[i + 1]![j] ?? 0) >= (dp[i]![j + 1] ?? 0)) i++
    else j++
  }
  return out
}

export interface Overlap {
  /** Stable within one comparison: the index of the hunk. */
  index: number
  base: string
  main: string
  branch: string
}

export type Pick = 'branch' | 'main'

/**
 * Merge `main` and `branch`, both descended from `base`. Returns the merged
 * text when every overlap has a pick, and the overlaps either way.
 */
export function mergeParagraphs(
  base: string,
  main: string,
  branch: string,
  picks: Record<number, Pick> = {},
): { text: string | null; overlaps: Overlap[] } {
  const B = splitParagraphs(base)
  const M = splitParagraphs(main)
  const R = splitParagraphs(branch)
  const toM = new Map(lcs(B, M))
  const toR = new Map(lcs(B, R))
  // Anchors: base paragraphs both sides kept unchanged.
  const anchors: [number, number, number][] = []
  for (let i = 0; i < B.length; i++) {
    const m = toM.get(i)
    const r = toR.get(i)
    if (m !== undefined && r !== undefined) anchors.push([i, m, r])
  }
  anchors.push([B.length, M.length, R.length])

  const out: string[] = []
  const overlaps: Overlap[] = []
  let complete = true
  let pb = 0
  let pm = 0
  let pr = 0
  for (const [ib, im, ir] of anchors) {
    const b = B.slice(pb, ib)
    const m = M.slice(pm, im)
    const r = R.slice(pr, ir)
    const same = (x: string[], y: string[]) => x.join('\n\n') === y.join('\n\n')
    if (same(m, b)) out.push(...r)
    else if (same(r, b) || same(m, r)) out.push(...m)
    else {
      const index = overlaps.length
      overlaps.push({
        index,
        base: joinParagraphs(b),
        main: joinParagraphs(m),
        branch: joinParagraphs(r),
      })
      const pick = picks[index]
      if (pick === 'main') out.push(...m)
      else if (pick === 'branch') out.push(...r)
      else complete = false
    }
    if (ib < B.length) out.push(B[ib] as string)
    pb = ib + 1
    pm = im + 1
    pr = ir + 1
  }
  return { text: complete ? joinParagraphs(out) : null, overlaps }
}

/** One place two texts differ, by paragraph: what went and what came. */
export interface Hunk {
  removed: string[]
  added: string[]
}

/** Where `after` differs from `before`, paragraph by paragraph. Pure. */
export function diffParagraphs(before: string, after: string): Hunk[] {
  const B = splitParagraphs(before)
  const N = splitParagraphs(after)
  const pairs = lcs(B, N)
  pairs.push([B.length, N.length])
  const out: Hunk[] = []
  let pb = 0
  let pn = 0
  for (const [ib, inew] of pairs) {
    const removed = B.slice(pb, ib).filter((p) => p.trim())
    const added = N.slice(pn, inew).filter((p) => p.trim())
    if (removed.length || added.length) out.push({ removed, added })
    pb = ib + 1
    pn = inew + 1
  }
  return out
}
